import type { WidgetCoreDefinition, TeXMLContext, TeXMLElement, ValidationResult } from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables } from './interpolate';

export type OutputHandle = {
  id: string;
  name: string;
  criteria: string;
};

export type AIAgentConfig = {
  personaId: string;
  maxTurns: number;
  inputType: string;
  timeout: number;
  outputHandles?: Array<{ id: string; name: string }>;
};

function generateTeXML(config: AIAgentConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);

  const existingHistory = context.variables[`_aiConversation_${context.nodeId}`];
  if (existingHistory) {
    return [
      {
        tag: 'Gather',
        attributes: {
          input: config.inputType || 'speech',
          timeout: config.timeout.toString(),
          action: callbackUrl,
          method: 'POST',
        },
      },
      {
        tag: 'Redirect',
        attributes: { method: 'POST' },
        content: callbackUrl,
      },
    ];
  }

  const greeting = context.variables._aiGreeting || 'Hello, how can I help you today?';
  const interpolatedGreeting = interpolateVariables(greeting, context.variables, context.speechFormats);

  return [
    {
      tag: 'Gather',
      attributes: {
        input: config.inputType || 'speech',
        timeout: config.timeout.toString(),
        action: callbackUrl,
        method: 'POST',
      },
      children: [
        {
          tag: 'Say',
          attributes: getVoiceAttributes(
            context.voiceConfig,
            context.variables._language,
            context.languageConfig,
            context.elevenLabsApiKeyRef,
          ),
          content: interpolatedGreeting,
        },
      ],
    },
    {
      tag: 'Redirect',
      attributes: { method: 'POST' },
      content: callbackUrl,
    },
  ];
}

function validateConfig(config: AIAgentConfig): ValidationResult {
  const errors: string[] = [];

  if (!config.personaId.trim()) {
    errors.push('Persona is required');
  }

  if (config.maxTurns < 1 || config.maxTurns > 50) {
    errors.push('Max turns must be between 1 and 50');
  }

  if (config.timeout < 1 || config.timeout > 30) {
    errors.push('Timeout must be between 1 and 30 seconds');
  }

  return { valid: errors.length === 0, errors };
}

export const aiAgentWidgetCore: WidgetCoreDefinition<AIAgentConfig> = {
  id: 'ai-agent',
  version: 1,
  name: 'AI Agent',
  description: 'Have an AI-driven conversation with the caller using a configured persona',
  category: 'integration',
  tier: 'premium',

  defaultConfig: {
    personaId: '',
    maxTurns: 10,
    inputType: 'speech',
    timeout: 10,
  },

  variables: {
    outputs: [
      {
        name: '_aiLastResponse',
        description: 'The last response from the AI agent',
        type: 'string',
        availableOnHandleTypes: ['ai-exit'],
      },
    ],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
  },

  validation: {
    validateConfig,
  },
};
